import { Component, createRef } from 'preact';
import { sorceryImageUrl } from '../utils/cardImageUrl';
import { RADIUS } from '../lib/medievalTheme';

// Dev-only tool (reached via `?dev=1&foilzones=1`) for authoring the
// rectangular regions where the foil white-amplification should apply.
// Applying the bloom to the whole card looks bad, so we mask it to a
// couple of zones (e.g. the name plate + the text box). Drag to draw up
// to two zones, then Copy to get their coordinates as fractions (0..1)
// of the card image, ready to paste into the mask config.
const MAX_ZONES = 2;
const IMG_WIDTH = 420; // display width; height follows the card aspect
const CARD_TYPES = ['Minion', 'Site', 'Avatar'];

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

function round4(n) {
  return Math.round(n * 10000) / 10000;
}

export default class FoilZoneEditor extends Component {
  state = {
    cardType: 'Minion',
    card: null,
    error: null,
    zones: [],
    drag: null, // { x0, y0, x1, y1 } in fractions while dragging
    copied: false,
  };

  surfaceRef = createRef();

  componentDidMount() {
    this.loadRandomFoil();
  }

  loadRandomFoil = async () => {
    const { cardType } = this.state;
    try {
      const res = await fetch('/api/sorcery/cards');
      const cards = await res.json();
      const arr = Array.isArray(cards) ? cards : (cards.cards || []);
      const foils = [];
      for (const c of arr) {
        if ((c.guardian?.type || c.type) !== cardType) continue;
        for (const set of c.sets || []) {
          for (const v of set.variants || []) {
            if (v.finish === 'Foil' && v.slug) foils.push({ name: c.name, slug: v.slug });
          }
        }
      }
      if (foils.length === 0) { this.setState({ error: `No foil ${cardType}s found in catalog.` }); return; }
      const pick = foils[Math.floor(Math.random() * foils.length)];
      this.setState({
        card: { name: pick.name, slug: pick.slug, imageUrl: sorceryImageUrl(pick.slug) },
        zones: [],
        drag: null,
        copied: false,
        error: null,
      });
    } catch (err) {
      this.setState({ error: `Failed to load cards: ${err?.message || err}` });
    }
  };

  pointToFraction = (e) => {
    const rect = this.surfaceRef.current.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  };

  handleMouseDown = (e) => {
    if (this.state.zones.length >= MAX_ZONES) return;
    e.preventDefault();
    const p = this.pointToFraction(e);
    this.setState({ drag: { x0: p.x, y0: p.y, x1: p.x, y1: p.y } });
  };

  handleMouseMove = (e) => {
    if (!this.state.drag) return;
    const p = this.pointToFraction(e);
    this.setState((s) => ({ drag: { ...s.drag, x1: p.x, y1: p.y } }));
  };

  handleMouseUp = () => {
    const d = this.state.drag;
    if (!d) return;
    const zone = {
      x: round4(Math.min(d.x0, d.x1)),
      y: round4(Math.min(d.y0, d.y1)),
      w: round4(Math.abs(d.x1 - d.x0)),
      h: round4(Math.abs(d.y1 - d.y0)),
    };
    // Ignore accidental clicks with no real area.
    if (zone.w < 0.01 || zone.h < 0.01) { this.setState({ drag: null }); return; }
    this.setState((s) => ({ zones: [...s.zones, zone].slice(0, MAX_ZONES), drag: null, copied: false }));
  };

  clearZones = () => this.setState({ zones: [], drag: null, copied: false });

  copyJson = async () => {
    const json = JSON.stringify(this.jsonOutput(), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch { /* textarea below is the fallback */ }
  };

  jsonOutput = () => ({
    type: this.state.cardType,
    card: this.state.card?.slug || null,
    zones: this.state.zones,
  });

  rectStyle = (z) => ({
    position: 'absolute',
    left: `${z.x * 100}%`,
    top: `${z.y * 100}%`,
    width: `${z.w * 100}%`,
    height: `${z.h * 100}%`,
  });

  render() {
    const { card, error, zones, drag, copied } = this.state;
    const inProgress = drag
      ? { x: Math.min(drag.x0, drag.x1), y: Math.min(drag.y0, drag.y1), w: Math.abs(drag.x1 - drag.x0), h: Math.abs(drag.y1 - drag.y0) }
      : null;

    return (
      <div style={{ position: 'fixed', inset: 0, background: '#14110c', color: '#e8d5a0', display: 'flex', gap: '24px', padding: '24px', fontFamily: 'system-ui, sans-serif', overflow: 'auto', zIndex: 99999 }}>
        <div>
          <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Foil White-Zone Editor</h1>
          <p style={{ fontSize: '12px', opacity: 0.7, marginBottom: '12px', maxWidth: `${IMG_WIDTH}px` }}>
            Drag to draw up to {MAX_ZONES} zones over the card. Coordinates are fractions (0..1) of the image. Then Copy.
          </p>

          {error ? <p style={{ color: '#ff8080' }}>{error}</p> : null}

          {card ? (
            <div
              ref={this.surfaceRef}
              onMouseDown={this.handleMouseDown}
              onMouseMove={this.handleMouseMove}
              onMouseUp={this.handleMouseUp}
              onMouseLeave={this.handleMouseUp}
              style={{ position: 'relative', width: `${IMG_WIDTH}px`, userSelect: 'none', cursor: zones.length >= MAX_ZONES ? 'not-allowed' : 'crosshair', lineHeight: 0 }}
            >
              <img src={card.imageUrl} alt={card.name} draggable={false} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: RADIUS.card }} />
              {zones.map((z, i) => (
                <div key={i} style={{ ...this.rectStyle(z), border: '2px solid #4ade80', background: 'rgba(74,222,128,0.22)', boxSizing: 'border-box' }}>
                  <span style={{ position: 'absolute', top: '-18px', left: 0, fontSize: '11px', color: '#4ade80', lineHeight: 1 }}>Zone {i + 1}</span>
                </div>
              ))}
              {inProgress ? (
                <div style={{ ...this.rectStyle(inProgress), border: '2px dashed #ffffff', background: 'rgba(255,255,255,0.18)', boxSizing: 'border-box' }} />
              ) : null}
            </div>
          ) : (!error ? <p>Loading…</p> : null)}

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', marginBottom: '8px' }}>
            {CARD_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => this.setState({ cardType: t }, this.loadRandomFoil)}
                style={{ ...btn, background: this.state.cardType === t ? 'rgba(74,222,128,0.25)' : btn.background, borderColor: this.state.cardType === t ? '#4ade80' : btn.border.split(' ')[2] }}
              >
                {t}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button onClick={this.clearZones} style={btn}>Clear zones</button>
            <button onClick={this.loadRandomFoil} style={btn}>New card</button>
            <button onClick={this.copyJson} disabled={zones.length === 0} style={{ ...btn, opacity: zones.length === 0 ? 0.4 : 1 }}>
              {copied ? 'Copied!' : 'Copy JSON'}
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: '260px' }}>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '6px' }}>
            {card ? `${card.name} (${card.slug}). ${zones.length}/${MAX_ZONES} zones` : ''}
          </div>
          <textarea
            readOnly
            value={JSON.stringify(this.jsonOutput(), null, 2)}
            style={{ width: '100%', height: '320px', background: '#0c0a07', color: '#9fe8b0', border: '1px solid rgba(180,140,60,0.4)', borderRadius: 'var(--radius-lg)', padding: '12px', fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}
          />
        </div>
      </div>
    );
  }
}

const btn = {
  background: 'rgba(180,140,60,0.18)',
  border: '1px solid rgba(180,140,60,0.5)',
  color: '#e8d5a0',
  borderRadius: 'var(--radius-md)',
  padding: '8px 14px',
  fontSize: '13px',
  cursor: 'pointer',
};
